import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, Typography, Badge, Input, message, Modal, Tooltip } from 'antd';
import { DollarOutlined, FileTextOutlined, CheckCircleOutlined, ClockCircleOutlined, PlusOutlined, EyeOutlined, ReloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission } from '../../utils/rolePermissions';
import { safeFormatDate } from '../../utils/timezone';
import logger from '../../services/logger';

const { Title, Text } = Typography;

const Billing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCustomer = user?.role?.name === 'customer';
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [searchText, setSearchText] = useState('');

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/v1/billing/invoices', { params: { limit: 200 } });
      // Backend düz array dönüyor
      const data = Array.isArray(response.data) ? response.data : (response.data.invoices || []);
      setInvoices(data);
    } catch (error) {
      logger.error('Faturalar yüklenirken hata:', error);
      message.error('Faturalar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvoices(); }, []);

  // Summary hesapla (frontend'de)
  const totalInvoices = invoices.length;
  const pendingInvoices = invoices.filter(i => i.status === 'pending').length;
  const paidInvoices = invoices.filter(i => i.status === 'paid').length;
  const totalAmount = invoices.reduce((sum, i) => sum + parseFloat(i.total_amount || 0), 0);

  const getStatusTag = (status) => {
    const config = {
      pending: { color: 'orange', text: 'Bekliyor' },
      paid: { color: 'green', text: 'Ödendi' },
      overdue: { color: 'red', text: 'Gecikmiş' },
    };
    const c = config[status] || { color: 'default', text: status };
    return <Tag color={c.color}>{c.text}</Tag>;
  };

  const handleDeleteInvoice = (record) => {
    Modal.confirm({
      title: 'Fatura Sil',
      content: `Fatura #${record.invoice_number} silinecek. Bu işlem geri alınamaz.`,
      okText: 'Sil',
      okType: 'danger',
      cancelText: 'Vazgeç',
      onOk: async () => {
        try {
          await axios.delete(`/api/v1/billing/invoices/${record.id}`);
          message.success('Fatura silindi');
          fetchInvoices();
        } catch (error) {
          message.error(error.response?.data?.detail || 'Fatura silinirken hata oluştu');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Fatura No', dataIndex: 'invoice_number', key: 'invoice_number', width: 180,
      render: (text, record) => <a onClick={() => navigate(`/billing/${record.id}`)}>{text}</a>,
    },
    {
      title: 'Müşteri', key: 'customer_name', width: 160,
      render: (_, record) => record.customer?.name || '-',
    },
    {
      title: 'Dönem', dataIndex: 'billing_period', key: 'billing_period', width: 100,
    },
    {
      title: 'Tutar', dataIndex: 'total_amount', key: 'total_amount', width: 120, align: 'right',
      render: (v) => v ? `₺${parseFloat(v).toFixed(2)}` : '-',
    },
    {
      title: 'Durum', dataIndex: 'status', key: 'status', width: 100,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Düzenlenme', dataIndex: 'issue_date', key: 'issue_date', width: 140,
      render: (d) => d ? safeFormatDate(d) : '-',
    },
    {
      title: 'İşlemler', key: 'actions', width: 100, align: 'center',
      render: (_, record) => (
        <Space size={0}>
          <Tooltip title="Görüntüle">
            <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/billing/${record.id}`)} />
          </Tooltip>
          {!isCustomer && (
            <Tooltip title="Sil">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteInvoice(record)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const getFilteredInvoices = () => {
    if (!searchText) return invoices;
    const s = searchText.toLowerCase();
    return invoices.filter(i =>
      i.invoice_number?.toLowerCase().includes(s) ||
      i.customer?.name?.toLowerCase().includes(s) ||
      i.status?.toLowerCase().includes(s) ||
      i.billing_period?.toLowerCase().includes(s) ||
      (i.total_amount && i.total_amount.toString().includes(s))
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <DollarOutlined /> Faturalandırma
        </Title>
      </div>

      {/* Stats */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space size="large" wrap>
          <span>
            <FileTextOutlined style={{ color: '#1890ff', marginRight: 6 }} />
            <Text style={{ marginRight: 4 }}>Toplam</Text>
            <Text strong style={{ fontSize: 18 }}>{totalInvoices}</Text>
          </span>
          <span>
            <Badge status="warning" />
            <Text style={{ marginRight: 4 }}>Bekleyen</Text>
            <Text strong style={{ color: '#fa8c16', fontSize: 18 }}>{pendingInvoices}</Text>
          </span>
          <span>
            <Badge status="success" />
            <Text style={{ marginRight: 4 }}>Ödenen</Text>
            <Text strong style={{ color: '#52c41a', fontSize: 18 }}>{paidInvoices}</Text>
          </span>
          <span>
            <DollarOutlined style={{ color: '#1890ff', marginRight: 6 }} />
            <Text style={{ marginRight: 4 }}>Toplam Tutar</Text>
            <Text strong style={{ fontSize: 18 }}>₺{totalAmount.toFixed(2)}</Text>
          </span>
        </Space>
      </Card>

      {/* Action Buttons + Search */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            {hasPermission(user?.role, 'billing.create_invoice') && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/billing/create')}>
                Yeni Fatura Oluştur
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={fetchInvoices} loading={loading}>Yenile</Button>

          </Space>
          <div style={{ minWidth: 400 }}>
            <Input.Search
              placeholder="Fatura no, müşteri adı, durum ara..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={getFilteredInvoices()}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `Toplam ${t} fatura` }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default Billing;
